<template>
  <div class="bg-orange-100 border border-orange-200 rounded p-4">
    <header
      class="flex space-x-2 items-center text-orange-800"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <!-- eslint-disable-next-line max-len -->
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      <h3 class="font-medium">Benefit Instruction Warnings</h3>
    </header>
    <section class="mt-2 text-sm space-y-2">
      <div v-for="(info,idx) in processedInfos" :key="idx">
        <h3 class="capitalize font-bold text-orange-900">{{info.type}}</h3>
        <div class="text-orange-800" v-html="info.html"></div>
      </div>
    </section>
  </div>
</template>

<script>
import _ from 'lodash';
import { getCurrentAge, getAgeAtSpecificDate } from '@/app/utils/utils';

import PensionInstructionMixin from '@/app/mixins/toolkit/pension-instructions/instruction';

class Info {
  constructor({
    type, template, html, isValid,
  }) {
    this.type = type;
    this.template = template || '';
    this.html = html || '';
    this.isValid = isValid;
  }
}

function getMemberAccounts(members) {
  return members.flatMap(member => _.values(member.accounts));
}
function getRuleAccounts(rules) {
  return rules.flatMap(rule => rule.accounts.ruleAccounts);
}

export const infos = [
  new Info({
    type: 'pension',
    html: "<p>Don't forget that minimum pension payments must be met - at least one of the payment rules should be to draw the minimum pension payments (usually we suggest this is the first rule).</p>",
    isValid({ members, rules }) {
      const minPensionRules = rules.filter(rule => rule.way.label === 'as pension payments');
      const memberAccounts = getMemberAccounts(members).filter(account => account.type === 'pension');
      const ruleAccounts = minPensionRules.flatMap(rule => rule.accounts.ruleAccounts).filter(account => account.type === 'pension');
      const memberAccountsIdentifiers = _.uniq(
        memberAccounts.flatMap(account => account.identifier),
      );
      const ruleAccountsIdentifiers = _.uniq(ruleAccounts.flatMap(account => account.identifier));

      return memberAccountsIdentifiers.length !== ruleAccountsIdentifiers.length;
    },
  }),
  new Info({
    type: 'pension',
    template: '<p>It looks like an account (<%member%> : <%identifier%>) has been excluded - you should consider at least include making the minimum payment from this pension as one of the rules.</p>',
    isValid({ members, rules }) {
      const ruleAccounts = getRuleAccounts(rules);
      let output = '';
      members.forEach((member) => {
        const memberAccounts = _.values(member.accounts).filter(account => account.type === 'pension');
        memberAccounts.forEach((account) => {
          // eslint-disable-next-line max-len
          if (!_.includes(ruleAccounts.map(ruleAccount => ruleAccount.identifier), account.identifier)) {
            let text = this.template;
            text = text.replace('<%member%>', member.name);
            text = text.replace('<%identifier%>', account.identifier);
            output += `${text}<br />`;
          }
        });
      });
      if (output !== '') {
        this.html = output;
        return true;
      }
      return false;
    },
  }),
  new Info({
    type: 'accumulation',
    html: "<p>You have nominated to have payments taken from one or more accumulation accounts.</p><p>Don't forget that if the member makes contributions for which he or she intends to claim a tax deduction, the notices to claim that deduction must be completed <strong>before</strong> any benefits are paid from the accumulation account. Otherwise, the tax deduction is reduced. If this is likely, you may wish to specify that payments are to be taken as pension commutations instead.  If necessary, the member can then commence additional pensions from the accumulation account(s) at the end of the year once the relevant deduction notices have been completed</p>",
    isValid({ members, rules }) {
      const ruleAccounts = rules.flatMap(rule => rule.accounts.ruleAccounts).filter(account => account.type === 'accumulation');
      return ruleAccounts.some((account) => {
        const ruleMember = members.filter(member => member.uuid === account.member.uuid)[0];
        return (ruleMember && ruleMember.ageAtEndOfYear < 75);
      });
    },
  }),
  new Info({
    type: 'pension',
    html: '<p>Check that the trust deed and any special conditions in place for the pension(s) allow commutations.</p>',
    isValid({ rules }) {
      const lumpSumPensionRules = rules.filter(rule => rule.way.label === 'as lump sum partial commutations');
      return lumpSumPensionRules.length > 0;
    },
  }),
  new Info({
    type: 'pension',
    html: '<p>Don\'t forget that transition to retirement pensions automatically become retirement phase pensions when the member is over 65.  Either correct this member\'s date of birth or change the pension type to "Account Based (Retirement Phase) Pension".</p>',
    isValid({ members }) {
      return members.some((member) => {
        const trisAccounts = _.values(member.accounts).filter(account => account.label === 'Transition to Retirement Pension');
        return trisAccounts.length > 0 && member.ageAtStartOfFinYear > 65;
      });
    },
  }),
  new Info({
    type: 'all',
    html: "<p>Make sure you consider the impact of these rules on estate planning.  For example, if different beneficiaries will inherit different accounts, make sure that these rules won't disrupt these plans.</p>",
    isValid() {
      return true;
    },
  }),
];

const getMembersAge = (members, memberUUID) => {
  const selectedMember = members.filter(member => member.uuid === memberUUID)[0];
  return selectedMember ? getCurrentAge(selectedMember.dateOfBirth) : null;
};


const getMemberAgeAtSpecificDate = (members, memberUUID, date) => {
  const selectedMember = members.filter(member => member.uuid === memberUUID)[0];
  return selectedMember ? getAgeAtSpecificDate(selectedMember.dateOfBirth, date) : null;
};

export default {
  name: 'payment-instruction-output',
  mixins: [PensionInstructionMixin],
  data() {
    return {
      show: false,
      infos,
    };
  },
  computed: {
    membersWithAge() {
      return this.members.map(member => ({
        ...member,
        age: getMembersAge(this.fund.members, member.uuid),
        // eslint-disable-next-line max-len
        ageAtEndOfYear: getMemberAgeAtSpecificDate(this.fund.members, member.uuid, new Date(new Date().getFullYear(), 11, 31)),
        // eslint-disable-next-line max-len
        ageAtStartOfFinYear: getMemberAgeAtSpecificDate(this.fund.members, member.uuid, new Date(new Date().getFullYear(), 6, 1)),
      }));
    },
    processedInfos() {
      return infos.filter(info => info.isValid({
        members: this.membersWithAge,
        rules: this.getItem.rules.filter(rule => typeof rule !== 'string'),
      }));
    },
  },
};
</script>
